const organizationUrlMaxLength = 512;

const dottedHostnamePattern = /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)+$/;

export type OrganizationUrlValidationError = "invalid" | "mustUseHttps" | "invalidHostname" | "maxLength";

export const getOrganizationUrlValidationError = (url: string): OrganizationUrlValidationError | null => {
    if (!url) {
        return null;
    }

    if (url.length > organizationUrlMaxLength) {
        return "maxLength";
    }

    // A bare domain (e.g. "acme.com") has no scheme for `new URL` to parse, so it would otherwise fall through
    // to the generic "invalid" catch below. Callers must type the scheme themselves; nothing normalizes it in
    // for them, so the error should point at that specifically rather than a generic parse failure.
    if (!url.includes("://")) {
        return "mustUseHttps";
    }

    try {
        const parsed = new URL(url);
        if (parsed.protocol !== "https:") {
            return "mustUseHttps";
        }
        if (parsed.href.length > organizationUrlMaxLength) {
            return "maxLength";
        }
        if (!dottedHostnamePattern.test(parsed.hostname)) {
            return "invalidHostname";
        }
        return null;
    } catch {
        return "invalid";
    }
};

export const getSafeExternalUrl = (url: string | null | undefined): string | null => {
    const trimmed = url?.trim();
    if (!trimmed) {
        return null;
    }
    return getOrganizationUrlValidationError(trimmed) === null ? trimmed : null;
};

const isValidUrl = (str: string): boolean => {
    try {
        return Boolean(new URL(str));
    } catch {
        return false;
    }
};

export const isValidZipUrl = (str: string): boolean => {
    return isValidUrl(str) && str.endsWith(".zip");
};

export const isValidApkUrl = (str: string): boolean => {
    return isValidUrl(str) && str.endsWith(".apk");
};

export const isValidSSHKey = (str: string): boolean => {
    const keyRegex = /^ssh-rsa [0-9A-Za-z+/]{128,}[=]{0,3}.*/;
    return keyRegex.test(str);
};
